import { useState, useEffect, useCallback } from 'react';
import type { AiAnalysisResponse } from './types';
import { getAiAnalysisResults } from '../../../services/CreditAnalysisService';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

const POLL_INTERVAL_MS = 5000;
// Statuses where polling should stop by default. NOT_STARTED means "never
// requested" so there is nothing to wait for. PROCESSING keeps polling.
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'NOT_STARTED']);

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface UseAiAnalysisPollingOptions {
    batchId: string;
    enabled: boolean;
    /** Change this value to force the polling loop to restart (e.g. after a new dispatch). */
    restartKey?: number;
    /**
     * When true, keep polling even if the backend still reports NOT_STARTED.
     * Used right after a dispatch, when results may not have landed yet and
     * NOT_STARTED would otherwise stop the loop and freeze the UI.
     */
    keepPollingOnNotStarted?: boolean;
}

interface UseAiAnalysisPollingReturn {
    data: AiAnalysisResponse | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
    /** The restartKey value in effect when the current `data` was fetched. */
    dataRestartKey: number;
}

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const isTerminal = (status: string | null, keepPollingOnNotStarted: boolean): boolean => {
    if (!status) return false;
    if (status === 'NOT_STARTED' && keepPollingOnNotStarted) return false;
    return TERMINAL_STATUSES.has(status);
};

// ═══════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════

export default function useAiAnalysisPolling({ batchId, enabled, restartKey = 0, keepPollingOnNotStarted = false }: UseAiAnalysisPollingOptions): UseAiAnalysisPollingReturn {
    const [data, setData] = useState<AiAnalysisResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Tracks which restartKey produced the current `data`, so callers can tell a
    // fresh (post-restart) response apart from a stale one left over from a
    // previous run. Prevents clearing "just dispatched" against old results.
    const [dataRestartKey, setDataRestartKey] = useState(restartKey);

    const fetchOnce = useCallback(async () => {
        try {
            const result = await getAiAnalysisResults(batchId);
            setData(result);
            setError(null);
        } catch {
            // refetch is best-effort; the polling loop surfaces persistent errors
        }
    }, [batchId]);

    useEffect(() => {
        if (!enabled || !batchId) return;

        // Local cancel flag scoped to this effect run. In-flight ticks check it
        // before writing state or scheduling the next poll, so a restart/unmount
        // fully invalidates the previous run (no stale writes or duplicated timers).
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const tick = async () => {
            if (cancelled) return;
            setLoading(true);
            try {
                const result = await getAiAnalysisResults(batchId);
                if (cancelled) return;
                setData(result);
                setDataRestartKey(restartKey);
                setError(null);
                if (!isTerminal(result.status ?? null, keepPollingOnNotStarted)) {
                    timer = setTimeout(tick, POLL_INTERVAL_MS);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Erro ao buscar resultados');
                // Retry on error until unmounted/disabled/restarted
                timer = setTimeout(tick, POLL_INTERVAL_MS);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        tick();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [enabled, batchId, restartKey, keepPollingOnNotStarted]);

    const refetch = useCallback(() => { fetchOnce(); }, [fetchOnce]);

    return { data, loading, error, refetch, dataRestartKey };
}
